import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, RotateCcw, History, ChevronRight, Volume2, TrendingUp, Calendar } from 'lucide-react';
import api from '../api/client';
import { toast } from 'react-hot-toast';
import { speakText as speakWithBestVoice } from '../utils/speech';

export default function ReviewHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const dictionaryType = location.state?.dictionaryType || localStorage.getItem('dictionaryType') || 'hsk';
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    fetchReviewedCards();
  }, [dictionaryType]);

  const fetchReviewedCards = async () => {
    try {
      const res = await api.get('/api/cards/reviewed', {
        params: { limit: 50, offset: 0, dictionary_type: dictionaryType }
      });
      setCards(res.data.cards);
      setTotalCount(res.data.total_count);
    } catch (error) {
      toast.error('Failed to load review history');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (cardId) => {
    setResetting(cardId);
    try {
      await api.post(`/api/cards/${cardId}/reset`);
      toast.success('Card reset successfully');
      // Update the card in the list
      setCards(prev => prev.map(c =>
        c.id === cardId
          ? { ...c, ease_factor: 2.5, interval: 0, repetitions: 0, next_review: null, last_reviewed: null }
          : c
      ));
    } catch (error) {
      toast.error('Failed to reset card');
    } finally {
      setResetting(null);
    }
  };

  const handleBulkReset = async () => {
    if (!cards.length) return;
    if (!window.confirm('Reset all cards to start learning them again?')) return;

    setResetting('all');
    try {
      await api.post('/api/cards/bulk/reset', cards.map(c => c.id));
      toast.success(`Reset ${cards.length} cards`);
      navigate('/');
    } catch (error) {
      toast.error('Failed to reset cards');
    } finally {
      setResetting(null);
    }
  };

  const speakChinese = (text) => {
    speakWithBestVoice(text, dictionaryType === 'ielts' ? 'en-US' : 'zh-CN');
  };

  const getRatingLabel = (rating) => {
    const labels = ['Again', 'Hard', 'Good', 'Easy'];
    return labels[rating] || 'Unknown';
  };

  const getRatingColor = (rating) => {
    const colors = [
      'bg-rose-100 text-rose-600 border-rose-200',
      'bg-amber-100 text-amber-600 border-amber-200',
      'bg-blue-100 text-blue-600 border-blue-200',
      'bg-emerald-100 text-emerald-600 border-emerald-200'
    ];
    return colors[rating] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between max-w-5xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 transition-colors bg-white/60 backdrop-blur-sm px-3 py-2 rounded-xl border border-slate-200 hover:border-indigo-200"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600" />
            Review History
          </h1>
          <span className="bg-indigo-100 text-indigo-600 px-3 py-1 rounded-full text-sm font-semibold">
            {totalCount}
          </span>
        </div>
        <button
          onClick={handleBulkReset}
          disabled={resetting !== null || !cards.length}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-b from-indigo-500 to-violet-500 text-white rounded-xl font-medium hover:from-indigo-600 hover:to-violet-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          <RotateCcw className="w-4 h-4" />
          Reset All
        </button>
      </div>

      {/* Cards List */}
      <div className="px-6 pb-8 max-w-5xl mx-auto">
        {cards.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="w-10 h-10 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">No History Yet</h2>
            <p className="text-slate-500 mb-8">
              Start studying and your review history will appear here!
            </p>
            <button
              onClick={() => navigate('/study')}
              className="px-6 py-3 bg-indigo-500 text-white rounded-xl font-medium hover:bg-indigo-600 transition-colors"
            >
              Start Studying
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {cards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl shadow-sm border-2 border-indigo-100 overflow-hidden hover:shadow-md hover:border-indigo-200 transition-all"
              >
                {/* Card Header */}
                <div
                  className="px-6 py-4 flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedCard(expandedCard === card.id ? null : card.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-left">
                      <h3 className="text-3xl font-bold bg-gradient-to-br from-slate-800 to-indigo-900 bg-clip-text text-transparent">
                        {card.word}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-slate-400">{card.pinyin}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            speakChinese(card.word);
                          }}
                          className="p-1 text-indigo-400 hover:text-indigo-600 transition-colors"
                        >
                          <Volume2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {card.hsk_level && (
                      <span className="px-3 py-1 bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-600 rounded-full text-xs font-semibold border border-indigo-200">
                        HSK {card.hsk_level}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Stats */}
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        <span>{card.repetitions}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{getTimeAgo(card.last_reviewed)}</span>
                      </div>
                    </div>

                    {/* Reset Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReset(card.id);
                      }}
                      disabled={resetting === card.id}
                      className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all disabled:opacity-50"
                      title="Reset card"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>

                    {/* Expand Chevron */}
                    <motion.div
                      animate={{ rotate: expandedCard === card.id ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </motion.div>
                  </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {expandedCard === card.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      <div className="border-t-2 border-indigo-100 bg-gradient-to-b from-indigo-50/50 to-white">
                        <div className="px-6 py-4 space-y-4">
                          {/* Meanings */}
                          <div className="space-y-2">
                            {card.meaning_en && (
                              <div className="flex items-start gap-3 bg-blue-50 rounded-xl px-4 py-3 border border-blue-100">
                                <span className="text-xs font-bold text-blue-500 mt-0.5 px-1.5 py-0.5 bg-blue-100 rounded">EN</span>
                                <p className="text-sm text-blue-800">{card.meaning_en}</p>
                              </div>
                            )}
                            {card.meaning_vi && (
                              <div className="flex items-start gap-3 bg-emerald-50 rounded-xl px-4 py-3 border border-emerald-100">
                                <span className="text-xs font-bold text-emerald-500 mt-0.5 px-1.5 py-0.5 bg-emerald-100 rounded">VI</span>
                                <p className="text-sm text-emerald-800">{card.meaning_vi}</p>
                              </div>
                            )}
                          </div>

                          {/* SM-2 Stats */}
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white rounded-xl p-3 border border-indigo-100 text-center">
                              <p className="text-xs text-slate-500 mb-1">Ease Factor</p>
                              <p className="text-lg font-bold text-indigo-600">{card.ease_factor.toFixed(2)}</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 border border-indigo-100 text-center">
                              <p className="text-xs text-slate-500 mb-1">Interval</p>
                              <p className="text-lg font-bold text-indigo-600">{card.interval}d</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 border border-indigo-100 text-center">
                              <p className="text-xs text-slate-500 mb-1">Repetitions</p>
                              <p className="text-lg font-bold text-indigo-600">{card.repetitions}</p>
                            </div>
                          </div>

                          {/* Recent Reviews */}
                          {card.recent_reviews && card.recent_reviews.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 mb-2">Recent Reviews</p>
                              <div className="flex flex-wrap gap-2">
                                {card.recent_reviews.map((review, idx) => (
                                  <div
                                    key={idx}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${getRatingColor(review.quality_rating)}`}
                                  >
                                    {getRatingLabel(review.quality_rating)}
                                    <span className="block text-[10px] opacity-75 mt-0.5">
                                      {getTimeAgo(review.reviewed_at)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
